/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { AwvInfo, AwvInfoConditionEnum } from "client";

export const createAwvIconDataUrl = (
  awvInfo?: AwvInfo,
  heading: number = 0,
  size: number = 100
) => {
  let fillColor = "white";
  if (awvInfo?.condition === AwvInfoConditionEnum.Error) {
    fillColor = "#F09090";
  } else if (awvInfo?.canAutonomous) {
    fillColor = "#90F090";
  }
  let rotation =
    360 -
    heading -
    (180 / Math.PI) * (awvInfo?.position?.postureInfo?.yawNed ?? 0);
  let badge = "";
  switch (awvInfo?.condition) {
    case AwvInfoConditionEnum.Running:
      badge = `<circle cx="82" cy="18" r="17" fill="#00C500" stroke="black" stroke-opacity="0.5"/>`;
      break;
    case AwvInfoConditionEnum.Stop:
    case AwvInfoConditionEnum.Charging:
    case AwvInfoConditionEnum.Ota:
      badge = `<circle cx="82" cy="18" r="17" fill="#333333" stroke="black" stroke-opacity="0.5"/>`;
      break;
    case AwvInfoConditionEnum.Error:
      badge = `<g transform="translate(64 0)">
        <path d="M0 32.0909H36L18 1L0 32.0909Z" fill="#F00000" stroke="black" stroke-opacity="0.5"/>
        <path d="M19.6365 27.1818H16.3638V23.9091H19.6365V27.1818ZM19.6365 20.6364H16.3638V14.0909H19.6365V20.6364Z" fill="white"/>
      </g>`;
      break;
    case AwvInfoConditionEnum.Offline:
      rotation = -90;
      badge = `<g transform="translate(64 0)">
        <circle cx="18" cy="18" r="17" fill="#999999"  stroke="black" stroke-opacity="0.5"/>
        <path d="M16.2438 20.456H19.0598C18.7298 17.354 22.5578 16.298 22.5578 13.174C22.5578 10.358 20.6218 8.994 17.9818 8.994C16.0678 8.994 14.5058 9.896 13.3618 11.194L15.1438 12.822C15.8478 12.096 16.6178 11.59 17.5858 11.59C18.7298 11.59 19.4558 12.294 19.4558 13.394C19.4558 15.506 15.7158 16.914 16.2438 20.456ZM17.6518 26.308C18.8178 26.308 19.6758 25.384 19.6758 24.196C19.6758 23.008 18.8178 22.084 17.6518 22.084C16.5078 22.084 15.6498 23.008 15.6498 24.196C15.6498 25.384 16.5078 26.308 17.6518 26.308Z" fill="white"/>
      </g>
      <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" fill="black" font-size="24px" font-family="Roboto,Helvetica,Arial,sans-serif">${
        awvInfo.nameId ?? "--"
      }</text>`;
      break;
  }
  return (
    "data:image/svg+xml;base64," +
    btoa(`
    <svg
      width="${size}"
      height="${size}"
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="rotate(${rotation} 50 50)">
        <path
          d="M27.4118 24C26.0835 24 25 25.0736 25 26.4062V37.6562C25 38.9889 26.0835 40.0625 27.4118 40.0625H30.2353C31.5636 40.0625 32.6471 38.9889 32.6471 37.6562V26.4062C32.6471 25.0736 31.5636 24 30.2353 24H27.4118ZM27.4118 63.375C26.0835 63.375 25 64.4486 25 65.7812V77.0312C25 78.3639 26.0835 79.4375 27.4118 79.4375H30.2353C31.5636 79.4375 32.6471 78.3639 32.6471 77.0312V65.7812C32.6471 64.4486 31.5636 63.375 30.2353 63.375H27.4118ZM69.7647 24C68.4364 24 67.3529 25.0736 67.3529 26.4062V37.6562C67.3529 38.9889 68.4364 40.0625 69.7647 40.0625H72.5882C73.9165 40.0625 75 38.9889 75 37.6562V26.4062C75 25.0736 73.9165 24 72.5882 24H69.7647ZM69.7647 63.375C68.4364 63.375 67.3529 64.4486 67.3529 65.7812V77.0312C67.3529 78.3639 68.4364 79.4375 69.7647 79.4375H72.5882C73.9165 79.4375 75 78.3639 75 77.0312V65.7812C75 64.4486 73.9165 63.375 72.5882 63.375H69.7647Z"
          fill="black"
          stroke="black"
          stroke-width="2"
        />
        <path
          d="M49.7408 0.741833C49.9001 0.645258 50.0999 0.645258 50.2592 0.741832L64.0011 9.07243C64.4338 9.3347 64.2478 10 63.7419 10H36.2581C35.7522 10 35.5663 9.3347 35.9989 9.07243L49.7408 0.741833Z"
          fill="white"
          stroke="black"
          stroke-linejoin="round"
        />
        <rect
          x="33.5"
          y="14.5"
          width="33"
          height="7"
          rx="1.5"
          fill="black"
          stroke="black"
        />
        <path
          d="M33 20C30.2386 20 28 22.2386 28 25V83C28 84.6569 29.3431 86 31 86H69C70.6569 86 72 84.6569 72 83V25C72 22.2386 69.7614 20 67 20H33Z"
          fill="${fillColor}"
          stroke="black"
          stroke-width="2"
        />
        <path
          opacity="0.25"
          d="M31 31H69V82C69 82.5523 68.5523 83 68 83H32C31.4477 83 31 82.5523 31 82V31Z"
          fill="black"
        />
      </g>
      ${badge}
    </svg>`)
  );
};
